import React from 'react';
import { usePouchDBContext } from './PouchDBProvider';

/**
 * PouchDB Status Component
 * 
 * This component displays the current status of PouchDB synchronization
 * and provides controls for manual sync and toggling sync mode.
 */
const PouchDBStatus = ({ showControls = true }) => {
  const {
    isInitialized,
    syncStatus,
    lastSynced,
    pendingChanges,
    isOnline,
    isSyncing,
    hasRemoteDB,
    syncEnabled,
    syncDatabases,
    toggleSync
  } = usePouchDBContext();

  if (!isInitialized) {
    return null; // Don't show anything while initializing
  }
  
  // If using API mode, show minimal status
  if (syncStatus === 'api-mode') {
    return (
      <div className="pouchdb-status">
        <div className="status-info">
          <div className="status-indicator">
            <span className={`status-dot ${isOnline ? 'online' : 'offline'}`}></span>
            <span>API Mode - {isOnline ? 'Connected' : 'Offline'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pouchdb-status">
      <div className="status-info">
        <div className="status-indicator">
          <span className={`status-dot ${isOnline ? 'online' : 'offline'}`}></span>
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </div>
        
        <div className="sync-status">
          <span>Sync: {syncEnabled ? 'Enabled' : 'Disabled'}</span>
          {pendingChanges > 0 && (
            <span className="pending-changes">
              {pendingChanges} pending {pendingChanges === 1 ? 'change' : 'changes'}
            </span>
          )}
        </div>
        
        {lastSynced && (
          <div className="last-synced">
            Last synced: {lastSynced.toLocaleTimeString()}
          </div>
        )}
      </div>
      
      {showControls && hasRemoteDB && (
        <div className="sync-controls">
          <button 
            onClick={syncDatabases} 
            disabled={isSyncing || !isOnline}
            className="sync-button"
          >
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
          
          <button 
            onClick={toggleSync}
            disabled={!isOnline && !syncEnabled}
            className="toggle-sync-button"
          >
            {syncEnabled ? 'Disable Auto-Sync' : 'Enable Auto-Sync'}
          </button>
        </div>
      )}
      
      {syncStatus === 'error' && (
        <div className="sync-error">
          Sync failed. Please try again.
        </div>
      )}
      
      <style jsx>{`
        .pouchdb-status {
          padding: 10px;
          border-radius: 4px;
          background-color: #f5f5f5;
          margin-bottom: 15px;
        }
        
        .status-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }
        
        .status-indicator {
          display: flex;
          align-items: center;
        }
        
        .status-dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-right: 5px;
        }
        
        .online {
          background-color: #4caf50;
        }
        
        .offline {
          background-color: #f44336;
        }
        
        .pending-changes {
          margin-left: 10px;
          color: #ff9800;
          font-weight: bold;
        }
        
        .sync-controls {
          display: flex;
          gap: 10px;
        }
        
        .sync-button, .toggle-sync-button {
          padding: 5px 10px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .sync-button {
          background-color: #2196f3;
          color: white;
        }
        
        .toggle-sync-button {
          background-color: #9e9e9e;
          color: white;
        }
        
        .sync-error {
          color: #f44336;
          margin-top: 10px;
        }
        
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default PouchDBStatus;